import { useEffect, useRef } from 'react';

import * as Logger from '@/lib/monitoring/logger';
import { useDashboardAccountSync } from '@/features/dashboard/hooks/use-dashboard-account-sync';
import { useDashboardSelectionStore } from '@/features/dashboard/stores/use-dashboard-selection-store';
import { useAccountState } from '@/contexts/account/account-context';
import { AccountStorage } from '@/features/workspace/api/account-storage/account-storage';
import { useEventsDebuggerStore } from '@/features/events/stores/use-events-debugger-store';

import { useTableColumnsAccountSync } from '@/features/dashboard/hooks/use-table-columns-account-sync';

export function useAccountStoreSync(): void {
  const previousAccountId = useRef<string | null>(null);
  const { accountId } = useAccountState();
  const syncStartTime = useRef<number>(0);

  useDashboardAccountSync();
  useTableColumnsAccountSync();

  useEffect(() => {
    Logger.debug('useAccountStoreSync effect triggered', {
      accountId,
      previousAccountId: previousAccountId.current,
      isSameAccount: previousAccountId.current === accountId,
    });

    if (previousAccountId.current === accountId) {
      Logger.debug('useAccountStoreSync skipping - same account', { accountId });
      return;
    }

    syncStartTime.current = performance.now();
    const isAccountSwitch = previousAccountId.current !== null && accountId !== null;

    Logger.debug('useAccountStoreSync starting sync', {
      accountId,
      previousAccountId: previousAccountId.current,
      isAccountSwitch,
    });

    if (isAccountSwitch) {
      Logger.debug('useAccountStoreSync clearing stores for account switch', {
        from: previousAccountId.current,
        to: accountId,
      });
      useEventsDebuggerStore.getState().destroy();
      useDashboardSelectionStore.getState().clearSelection();
    }

    if (accountId) {
      Logger.debug('useAccountStoreSync switching AccountStorage', { accountId });
      AccountStorage.switchAccount(accountId);
      AccountStorage.pruneOldAccounts();
    }

    previousAccountId.current = accountId;

    const durationMs = Math.round(performance.now() - syncStartTime.current);
    Logger.debug('useAccountStoreSync complete', {
      accountId,
      isAccountSwitch,
      durationMs,
    });
  }, [accountId]);
}
